// THE ONE CLOCK FORMAT (owner, 15 Oct 2026).
//
// Every time of day the product SHOWS is 12-hour with AM/PM — "2:30 PM" — on
// every screen, in every language, whatever the browser or phone locale says.
// The calendar used to say "2pm" beside a card saying "14:30", availability
// was 24-hour and the appointment list AM/PM; a doctor reading three formats
// on one page is the bug.
//
// DISPLAY ONLY. What is stored and sent stays 24-hour "HH:mm" (availability,
// slots, leave, payloads). Never feed these results back into a payload, a
// comparison or a sort — format at render time, from the 24-hour value.
//
// Locale-free on purpose: no toLocaleTimeString, no 'h:mm a' in a locale that
// may answer "14:30", and no Intl output for the final string (newer ICU puts
// a narrow no-break space before "PM", which would differ by engine).
//
// The same file is copied into every client (Frontend-web, Super Admin,
// patient web, Pro-App, Patient App, Meet); Backend/utils/clockFormat.js is the
// server's copy. Change them together.

const pad2 = (n) => String(n).padStart(2, '0');

const fromHourMinute = (hour, minute) => {
    const h = Number(hour) % 24;
    if (!Number.isInteger(h) || h < 0) return '';
    return `${h % 12 === 0 ? 12 : h % 12}:${pad2(minute)} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * A WALL-CLOCK string -> "2:30 PM". Takes "14:30", "14:30:00", "9:05" or a
 * local date-time string like "2026-10-15T14:30" / "2026-10-15 14:30".
 * NOT for a UTC instant ("…Z" / "+05:30") — use formatTime for those.
 * Text already in AM/PM, and anything it cannot read, comes back as it
 * was; null / '' give ''.
 */
export function formatClock(value) {
    if (value === null || value === undefined || value === '') return '';
    // Already in this format ("2:30 PM", "2:30pm") — returned as it is: read
    // again it would lose its PM and come back "2:30 AM".
    if (/\d\s*[ap]\.?m\.?(?![a-z])/i.test(String(value))) return String(value);
    const m = /(?:^|T|\s)(\d{1,2}):(\d{2})/.exec(String(value));
    if (!m || Number(m[1]) > 23) return String(value);
    return fromHourMinute(m[1], m[2]);
}

/** Two wall-clock strings -> "2:30 PM – 3:00 PM" (either side may be missing). */
export function formatClockRange(start, end) {
    const a = formatClock(start);
    const b = formatClock(end);
    return a && b ? `${a} – ${b}` : (a || b);
}

/**
 * An INSTANT (Date, ISO string, epoch ms) -> "2:30 PM", in `timeZone` (an
 * IANA name) when given, else the device's zone. '' when it is not a date.
 */
export function formatTime(value, { timeZone } = {}) {
    if (value === null || value === undefined || value === '') return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    if (timeZone) {
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
            }).formatToParts(d);
            const hour = parts.find((p) => p.type === 'hour')?.value;
            const minute = parts.find((p) => p.type === 'minute')?.value;
            if (hour !== undefined && minute !== undefined) return fromHourMinute(hour, minute);
        } catch {
            // An unknown zone falls through to the device's clock.
        }
    }
    return fromHourMinute(d.getHours(), d.getMinutes());
}

/** Two instants -> "2:30 PM – 3:00 PM". */
export function formatTimeRange(start, end, opts = {}) {
    const a = formatTime(start, opts);
    const b = formatTime(end, opts);
    return a && b ? `${a} – ${b}` : (a || b);
}

/** An hour 0–23 -> "12 AM", "1 AM" … "11 PM" — a picker's hour label. */
export function formatHourLabel(hour) {
    const h = Number(hour);
    if (!Number.isInteger(h) || h < 0 || h > 23) return String(hour);
    return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
}
